<template>
    <b-modal
            v-model="modal.state"
            body-class="bg-dark d-flex flex-column justify-content-between h-100"
            content-class="border-0"
            footer-class="bg-dark p-2 p-sm-3"
            header-class="d-flex flex-row p-2"
            modal-class="property-details-modal p-0"
            no-close-on-backdrop
            scrollable
            @close="hideModal"
    >
        <template
                slot="modal-header"
                slot-scope="{close}"
        >
            <div class="tabs-group">
                <div class="align-items-center">
                    <b-button-group size="lg">
                        <b-button
                                v-if="images.length"
                                :pressed="isTabActive(TABS.PHOTOS)"
                                @click="handleChangeTab(TABS.PHOTOS)"
                        >
                            Photos
                        </b-button>
                        <b-button
                                :pressed="isTabActive(TABS.MAP)"
                                @click="handleChangeTab(TABS.MAP)"
                        >
                            Map
                        </b-button>
                        <b-button
                                v-if="streetViewAvailable"
                                :pressed="isTabActive(TABS.STREET_VIEW)"
                                @click="handleChangeTab(TABS.STREET_VIEW)"
                        >
                            Street View
                        </b-button>
                    </b-button-group>
                </div>
            </div>
            <property-details-favorite-action
                    v-if="favoriteOptionEnabled"
                    :favorite-status="favoriteStatus"
                    :is-large-button="true"
                    :property-app-resource="propertyAppResource"
            />
            <b-button
                    class="icon-button text-gray-700 fal fa-times ml-3"
                    size="md"
                    type="button"
                    variant="ternary"
                    @click="close"
            />
        </template>
        <template slot="default">
            <div class="body-wrapper">
                <b-carousel
                        v-if="isTabActive(TABS.PHOTOS)"
                        id="imageGalleryCarousel"
                        ref="imageGalleryCarousel"
                        v-model="imageIndex"
                        :interval="0"
                        class="mb-0 h-100"
                        controls
                >
                    <b-carousel-slide
                            v-for="(image, index) in images"
                            :key="index"
                            :img-src="image"
                    />
                </b-carousel>
                <google-map-loader
                        v-if="isTabActive(TABS.MAP)"
                        :map-config="MAP_CONFIG"
                        :markers="markers"
                        center-on-marker
                />
                <google-street-view-map-loader
                        v-if="isTabActive(TABS.STREET_VIEW) && streetViewAvailable"
                        :map-config="MAP_CONFIG"
                        :marker="markers[0]"
                />
            </div>
        </template>
        <template slot="modal-footer">
            <div
                    class="carousel-inner my-0 mx-auto"
            >
                <div
                        class="carousel-caption py-0"
                >
                    <div v-html="getCaptionHtml(isTabActive(TABS.PHOTOS))" />
                </div>
            </div>
        </template>
    </b-modal>
</template>

<script>
    import './PropertyDetailsModal.scss';
    import {niceNumberFormatFilter} from '../../../filters/niceNumberFormat.filter';
    import {SIZE_UNIT_MAPING} from '../../../../helpers/variables';
    import {LatLng} from "../../../GoogleMaps/classes/LatLng";
    import {SimplePropertyMarker} from "../../../GoogleMaps/classes/SimplePropertyMarker";
    import GoogleMapLoader from '../../../GoogleMaps/components/GoogleMapLoader/GoogleMapLoader';
    import GoogleStreetViewMapLoader
        from "../../../GoogleMaps/components/GoogleMapStreetViewLoader/GoogleMapStreetViewLoader";
    import PropertyDetailsFavoriteAction
        from "../PropertyDetailsActions/PropertyDetailsFavoriteAction/PropertyDetailsFavoriteAction";
    import GoogleMapsLoader from "../../../classes/GoogleMapsLoader";

    export const PROPERTY_DETAILS_MODAL_DISPLAY = 'PropertyDetailsModal:display';
    export const TABS = {
        PHOTOS: 'photos',
        MAP: 'map',
        STREET_VIEW: 'street_view',
        COMMUTE: 'commute',
        SCHOOLS: 'schools',
    };
    const MAP_CONFIG = {
        disableDefaultUI: true,
        fullscreenControl: false,
        styles: [
            {
                'featureType': 'poi',
                'stylers': [
                    {
                        'visibility': 'off',
                    },
                ],
            },
        ],
        zoom: 15,
    };
    export default {
        name: "PropertyDetailsModal",
        components: {
            PropertyDetailsFavoriteAction,
            GoogleStreetViewMapLoader,
            GoogleMapLoader
        },
        props: {
            favoriteStatus: {
                type: Boolean,
                default: false,
            },
            favoriteOptionEnabled: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                MAP_CONFIG,
                TABS,
                modal: {
                    state: false,
                },
                propertyAppResource: null,
                activeTab: TABS.PHOTOS,
                imageIndex: null,
                streetViewAvailable: false,
                propertyImages: [],
                propertyImagesLoaded: false,
            };
        },
        computed: {
            images(){
                return this.propertyImages.map(propertyImg => {
                    let url = new URL(propertyImg);
                    url.searchParams.append('width', 1120);
                    url.searchParams.append('height', 750);
                    return url.href;
                });
            },
            sizeUnit() {
                return this.propertyAppResource.sizeUnits &&
                    SIZE_UNIT_MAPING.hasOwnProperty(this.propertyAppResource.sizeUnits)
                    ? SIZE_UNIT_MAPING[this.propertyAppResource.sizeUnits]
                    : '';
            },
            markers() {
                if (!this.propertyAppResource ||
                    !this.propertyAppResource.details.latitude ||
                    !this.propertyAppResource.details.longitude) {
                    return [];
                }
                const latLng = new LatLng(this.propertyAppResource.details.latitude, this.propertyAppResource.details.longitude);
                return [new SimplePropertyMarker(latLng, this.propertyAppResource)];
            },
            isFavorite() {
                return this.favorite;
            }
        },
        methods: {
            hideModal() {
                this.modal.state = false;
                this.propertyAppResource = null;
                this.activeTab = TABS.PHOTOS;
            },
            onModalDisplay({property, tab, imageIndex = null}) {
                this.propertyAppResource = property;
                this.initGoogleItems();

                this.modal.state = true;
                this.activeTab = tab;
                this.imageIndex = imageIndex;
            },
            async initGoogleItems(){
                let googleMapsLoader = GoogleMapsLoader.instance();
                googleMapsLoader.load();
                await googleMapsLoader.importLibrary('maps');
                this.checkStreetViewAvailability();
                this.setPropertyImages();
            },
            handleChangeTab(tab) {
                this.activeTab = tab;
            },
            isTabActive(tab) {
                return this.activeTab === tab;
            },
            getCaptionHtml(showImageCounter = false) {
                if (!this.propertyAppResource) {
                    return;
                }

                let details = '<div class="d-inline-flex">';
                if (this.propertyAppResource.price !== null) {
                    details += `<span class='separator d-none d-sm-block'>|</span>`;
                    details += `<p class='strong mb-0'>$${niceNumberFormatFilter(this.propertyAppResource.price)}</p>`;
                }
                if (this.propertyAppResource.bedrooms !== null) {
                    details += `<span class='separator'>|</span>`;
                    details += `<p class='mb-0'>${this.propertyAppResource.bedrooms} beds</p>`;
                }
                if (this.propertyAppResource.bathrooms !== null) {
                    details += `<span class='separator'>|</span>`;
                    details += `<p class='mb-0'>${this.propertyAppResource.bathrooms} baths</p>`;
                }
                if (this.propertyAppResource.size !== null) {
                    details += `<span class='separator'>|</span>`;
                    details += `<p class='mb-0'>${niceNumberFormatFilter(this.propertyAppResource.size)} </p>`;
                }
                if (this.sizeUnit) {
                    details += `<p class='mb-0 ml-1'>${this.sizeUnit}</p>`;
                }
                details += '</div>';


                if (this.propertyAppResource.addresses && this.propertyAppResource.addresses.isNotEmpty) {
                    details = `<p class='mb-0'>${this.propertyAppResource.addresses.getFirst().street}</p>` + details;
                }

                let caption = '';

                if (showImageCounter) {
                    caption = `<div class="d-flex justify-content-between justify-content-sm-center align-items-center">
                                        <h3 class='text-white mb-0 mr-3'>${this.imageIndex + 1}/${this.propertyImages.length}</h3>
                                        <div class="text-right text-sm-center d-block d-sm-inline-flex">
                                            ${details}
                                        </div>
                                    </div>`;
                } else {
                    caption += `<div class="d-flex justify-content-center">
                                        <div class="center d-block d-sm-inline-flex">
                                            ${details}
                                        </div>
                                    </div>`;
                }

                return caption;
            },

            prev() {
                this.$refs.imageGalleryCarousel.prev();
            },

            next() {
                this.$refs.imageGalleryCarousel.next();
            },

            addKeydownListener() {
                window.addEventListener("keydown", this.handleArrowKeyPressedEvent);
            },


            removeKeydownListener() {
                window.removeEventListener("keydown", this.handleArrowKeyPressedEvent);
            },

            handleArrowKeyPressedEvent(keyboardEvent) {
                if (
                    document.body.classList.contains('modal-open') &&
                    document.getElementById('imageGalleryCarousel')
                ) {
                    switch (keyboardEvent.code) {
                        case 'ArrowLeft':
                            this.prev();
                            break;
                        case 'ArrowRight':
                            this.next();
                            break;
                        default:
                            return;
                    }
                }
            },

            async checkStreetViewAvailability() {
                const panorams = await this.propertyAppResource.getGooglePanorama();
                this.streetViewAvailable = Boolean(panorams.length);

            },
            async setPropertyImages() {
                this.propertyImages = await this.propertyAppResource.getImages(3);
                this.propertyImagesLoaded = true;
            },
        },

        async beforeMount() {
            this.$bus.$on(PROPERTY_DETAILS_MODAL_DISPLAY, this.onModalDisplay);
            this.addKeydownListener();
        },

        beforeDestroy() {
            this.$bus.$off(PROPERTY_DETAILS_MODAL_DISPLAY, this.onModalDisplay);
            this.removeKeydownListener();
        },
    };
</script>
